/**
 * IndexedDB Utility
 *
 * Provides a robust IndexedDB wrapper for storing application data.
 * Replaces localStorage for better performance, larger storage capacity,
 * and structured data storage.
 *
 * Features:
 * - Async/Promise-based API
 * - Automatic schema versioning and migrations
 * - TTL (Time-To-Live) support for cache expiration
 * - Batch operations for performance
 * - Error handling and fallback mechanisms
 */

const DB_NAME = 'extranet-cache-db';
const DB_VERSION = 4; // Bumped for API response cache support

// Store names for different data types
export const STORES = {
  CARGO_LIST: 'cargo-list',
  CARGO_COUNT: 'cargo-count',
  BALTIC: 'baltic',
  BUNKER_PRICES: 'bunker-prices',
  FIXED_DAYS: 'fixed-days',
  POSITION_LIST: 'position-list',
  FIXTURES: 'fixtures',
  RECENT_FIXTURES: 'recent-fixtures',
  USER_GROUPS: 'user-groups',
  REFERENCE_DATA: 'reference-data',
  REPORTS: 'reports',
  USER: 'user',
  THEME: 'theme',
  DAILY_REPORTS: 'daily-reports',
  API_RESPONSES: 'api-responses',
} as const;

export type StoreName = (typeof STORES)[keyof typeof STORES];

export interface CacheEntry<T = unknown> {
  key: string;
  value: T;
  timestamp: number;
  expiresAt?: number;
}

export interface IndexedDBConfig {
  ttl?: number; // Time to live in milliseconds
}

class IndexedDBManager {
  private dbPromise: Promise<IDBDatabase> | null = null;
  private isSupported: boolean;

  constructor() {
    this.isSupported = this.checkSupport();
  }

  /**
   * Check if IndexedDB is supported
   */
  private checkSupport(): boolean {
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        return false;
      }

      // Check if indexedDB is available
      if (!('indexedDB' in window)) {
        return false;
      }

      // Check if indexedDB is not null/undefined
      const idb = window.indexedDB;
      if (!idb || idb === null || idb === undefined) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Initialize database connection
   */
  private initDB(): Promise<IDBDatabase> {
    if (!this.isSupported) {
      return Promise.reject(new Error('IndexedDB is not supported'));
    }

    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores for each data type
        Object.values(STORES).forEach((storeName) => {
          if (!db.objectStoreNames.contains(storeName)) {
            const store = db.createObjectStore(storeName, { keyPath: 'key' });
            store.createIndex('timestamp', 'timestamp', { unique: false });
            store.createIndex('expiresAt', 'expiresAt', { unique: false });
            console.log(`[IndexedDB] Created object store: ${storeName}`);
          }
        });
      };
    });

    return this.dbPromise;
  }

  /**
   * Get a value from the cache
   */
  async get<T>(storeName: StoreName, key: string): Promise<T | null> {
    if (!this.isSupported) return null;

    try {
      const db = await this.initDB();

      // Check if store exists before creating transaction
      if (!db.objectStoreNames.contains(storeName)) {
        console.warn(
          `[IndexedDB] Store '${storeName}' not found. Database may need upgrade. Please refresh the page.`
        );
        return null;
      }

      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const entry = request.result as CacheEntry<T> | undefined;

          if (!entry) {
            resolve(null);
            return;
          }

          // Check if entry has expired
          if (entry.expiresAt && Date.now() > entry.expiresAt) {
            // Entry expired, delete it and return null
            this.delete(storeName, key);
            resolve(null);
            return;
          }

          resolve(entry.value);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('[IndexedDB] Get error:', error);
      return null;
    }
  }

  /**
   * Set a value in the cache
   */
  async set<T>(
    storeName: StoreName,
    key: string,
    value: T,
    config?: IndexedDBConfig
  ): Promise<void> {
    if (!this.isSupported) return;

    try {
      const db = await this.initDB();

      // Check if store exists before creating transaction
      if (!db.objectStoreNames.contains(storeName)) {
        console.error(
          `[IndexedDB] Store '${storeName}' not found. Database may need upgrade. Please refresh the page.`
        );
        throw new Error(
          `Store '${storeName}' not found. Please refresh the page to upgrade the database.`
        );
      }

      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      const entry: CacheEntry<T> = {
        key,
        value,
        timestamp: Date.now(),
        expiresAt: config?.ttl ? Date.now() + config.ttl : undefined,
      };

      const request = store.put(entry);

      return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('[IndexedDB] Set error:', error);
      throw error;
    }
  }

  /**
   * Delete a value from the cache
   */
  async delete(storeName: StoreName, key: string): Promise<void> {
    if (!this.isSupported) return;

    try {
      const db = await this.initDB();

      // Check if store exists before creating transaction
      if (!db.objectStoreNames.contains(storeName)) {
        console.warn(`[IndexedDB] Store '${storeName}' not found. Skipping delete.`);
        return;
      }

      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('[IndexedDB] Delete error:', error);
    }
  }

  /**
   * Clear all data from a store
   */
  async clear(storeName: StoreName): Promise<void> {
    if (!this.isSupported) return;

    try {
      const db = await this.initDB();

      // Check if store exists before creating transaction
      if (!db.objectStoreNames.contains(storeName)) {
        console.warn(`[IndexedDB] Store '${storeName}' not found. Skipping clear.`);
        return;
      }

      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          console.log(`[IndexedDB] Cleared store: ${storeName}`);
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('[IndexedDB] Clear error:', error);
    }
  }

  /**
   * Get all keys from a store
   */
  async getAllKeys(storeName: StoreName): Promise<string[]> {
    if (!this.isSupported) return [];

    try {
      const db = await this.initDB();

      // Check if store exists before creating transaction
      if (!db.objectStoreNames.contains(storeName)) {
        console.warn(`[IndexedDB] Store '${storeName}' not found. Returning empty array.`);
        return [];
      }

      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAllKeys();

      return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result as string[]);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('[IndexedDB] GetAllKeys error:', error);
      return [];
    }
  }

  /**
   * Get all values from a store
   */
  async getAll<T>(storeName: StoreName): Promise<T[]> {
    if (!this.isSupported) return [];

    try {
      const db = await this.initDB();

      // Check if store exists before creating transaction
      if (!db.objectStoreNames.contains(storeName)) {
        console.warn(`[IndexedDB] Store '${storeName}' not found. Returning empty array.`);
        return [];
      }

      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const entries = request.result as CacheEntry<T>[];
          const now = Date.now();

          // Filter out expired entries
          const validEntries = entries
            .filter((entry) => !entry.expiresAt || now <= entry.expiresAt)
            .map((entry) => entry.value);

          resolve(validEntries);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('[IndexedDB] GetAll error:', error);
      return [];
    }
  }

  /**
   * Delete expired entries from a store
   */
  async clearExpired(storeName: StoreName): Promise<number> {
    if (!this.isSupported) return 0;

    try {
      const db = await this.initDB();

      // Check if store exists before creating transaction
      if (!db.objectStoreNames.contains(storeName)) {
        console.warn(`[IndexedDB] Store '${storeName}' not found. Skipping clearExpired.`);
        return 0;
      }

      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const index = store.index('expiresAt');
      const now = Date.now();

      // Get all entries with expiresAt index
      const request = index.openCursor();
      let deletedCount = 0;

      return new Promise((resolve, reject) => {
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result as IDBCursorWithValue;

          if (cursor) {
            const entry = cursor.value as CacheEntry;
            if (entry.expiresAt && entry.expiresAt < now) {
              cursor.delete();
              deletedCount++;
            }
            cursor.continue();
          } else {
            resolve(deletedCount);
          }
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      return 0;
    }
  }

  /**
   * Clear all expired entries from all stores
   */
  async clearAllExpired(): Promise<number> {
    if (!this.isSupported) return 0;

    let totalDeleted = 0;
    for (const storeName of Object.values(STORES)) {
      const deleted = await this.clearExpired(storeName);
      totalDeleted += deleted;
    }

    return totalDeleted;
  }

  /**
   * Get storage statistics for a store
   */
  async getStats(storeName: StoreName): Promise<{
    count: number;
    expiredCount: number;
    sizeEstimate: number;
  }> {
    if (!this.isSupported) {
      return { count: 0, expiredCount: 0, sizeEstimate: 0 };
    }

    try {
      const db = await this.initDB();
      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const entries = request.result as CacheEntry[];
          const now = Date.now();
          let expiredCount = 0;
          let sizeEstimate = 0;

          entries.forEach((entry) => {
            if (entry.expiresAt && entry.expiresAt < now) {
              expiredCount++;
            }
            // Rough estimate of size (JSON stringification)
            sizeEstimate += JSON.stringify(entry.value).length;
          });

          resolve({
            count: entries.length,
            expiredCount,
            sizeEstimate,
          });
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('[IndexedDB] GetStats error:', error);
      return { count: 0, expiredCount: 0, sizeEstimate: 0 };
    }
  }

  /**
   * Clear all data from all stores
   */
  async clearAll(): Promise<void> {
    if (!this.isSupported) return;

    console.log('[IndexedDB] Clearing all stores...');

    for (const storeName of Object.values(STORES)) {
      await this.clear(storeName);
    }

    console.log('[IndexedDB] All stores cleared');
  }

  /**
   * Delete the entire database
   */
  async deleteDatabase(): Promise<void> {
    if (!this.isSupported) return;

    try {
      // Close existing connection
      if (this.dbPromise) {
        const db = await this.dbPromise;
        db.close();
        this.dbPromise = null;
      }

      return new Promise((resolve, reject) => {
        const request = window.indexedDB.deleteDatabase(DB_NAME);

        request.onsuccess = () => {
          console.log('[IndexedDB] Database deleted successfully');
          resolve();
        };

        request.onerror = () => {
          console.error('[IndexedDB] Failed to delete database:', request.error);
          reject(request.error);
        };

        request.onblocked = () => {
          console.warn('[IndexedDB] Database deletion blocked by open connections');
        };
      });
    } catch (error) {
      console.error('[IndexedDB] DeleteDatabase error:', error);
    }
  }

  /**
   * Check if database is available and ready
   */
  async isAvailable(): Promise<boolean> {
    // First check if IndexedDB is supported at all
    if (!this.isSupported) {
      return false;
    }

    try {
      // Try to initialize the database
      await this.initDB();
      return true;
    } catch (error) {
      console.debug('[IndexedDB] Not available:', error);
      return false;
    }
  }
}

// Export singleton instance
export const indexedDB = new IndexedDBManager();
export default indexedDB;
